import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TabTitleService } from '@core/tab-titles/tab-title-service';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageItems } from '@shared/constants/local-storage-item';
import { TranslationLanguage } from '@shared/constants/translation-lang';
import { BehaviorSubject, filter, Observable, of, switchMap, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private tabTitleService = inject(TabTitleService);
  private router = inject(Router);

  loadingTranslations$ = new BehaviorSubject<boolean>(true);

  setTranslation(lang: TranslationLanguage): Observable<boolean> {
    this.loadingTranslations$.next(true);

    return this.downloadTranslation(
      lang === TranslationLanguage.Development ? TranslationLanguage.EN : lang
    ).pipe(
      switchMap(translations => {
        if (lang === TranslationLanguage.Development) {
          const developmentTranslations = translations.map(translation => ({
            ...translation,
            value: translation.key,
          }));
          this.setNewLanguage(developmentTranslations, lang);
        } else {
          this.setNewLanguage(translations, lang);
        }

        return of(true);
      })
    );
  }

  downloadTranslation(lang = 'no'): Observable<TranslationItem[]> {
    return this.http.get<TranslationItem[]>(
      'https://translation-api.devinco.com/translate/3/' + lang
    );
  }

  setupTabTitles(): void {
    this.loadingTranslations$
      .pipe(
        filter(loading => !loading),
        take(1)
      )
      .subscribe(() => {
        this.tabTitleService.setupTabTitles();
      });
  }

  private setNewLanguage(
    translations: TranslationItem[],
    lang: TranslationLanguage
  ): void {
    // Build translation object
    const langObj: { [key: string]: string } = {};
    translations.forEach(item => (langObj[item.key] = item.value));
    // Use translation
    this.translate.setTranslation(lang, langObj);
    this.translate.use(lang);
    localStorage.setItem(LocalStorageItems.Language, lang);
    this.loadingTranslations$.next(false);
    this.tabTitleService.setTabTitle(this.router.url);
    document.documentElement.lang = lang.toLowerCase();
  }
}

export interface TranslationItem {
  key: string;
  textId: number;
  value: string;
}
